import { useCallback, useEffect, useRef, useState } from "react";
import { portalConfig } from "../config/applications";
import type { ApplicationStatus, ApplicationStatusResponse } from "../types/portal";

const initialStatus: Omit<ApplicationStatus, "refresh"> = {
  state: "checking",
  online: false,
  statusCode: null,
  authenticationRequired: false,
  checkedAt: "",
  isLoading: true,
  error: null,
};

export function useApplicationStatus(endpoint: string | undefined, enabled: boolean): ApplicationStatus {
  const [status, setStatus] = useState(initialStatus);
  const inFlight = useRef<Promise<void> | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  const refresh = useCallback((): Promise<void> => {
    if (!enabled || !endpoint) return Promise.resolve();
    if (inFlight.current) return inFlight.current;

    const controller = new AbortController();
    abortController.current = controller;
    setStatus((current) => ({ ...current, isLoading: true, error: null }));

    const request = fetch(endpoint, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Status service unavailable");
        return (await response.json()) as ApplicationStatusResponse;
      })
      .then((data) => {
        if (!mounted.current) return;
        setStatus({
          state: data.state,
          online: data.online,
          statusCode: data.statusCode,
          authenticationRequired: data.authenticationRequired,
          checkedAt: data.checkedAt,
          isLoading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (!mounted.current || controller.signal.aborted) return;
        setStatus({
          state: "offline",
          online: false,
          statusCode: null,
          authenticationRequired: false,
          checkedAt: new Date().toISOString(),
          isLoading: false,
          error: error instanceof Error ? error.message : "Status service unavailable",
        });
      })
      .finally(() => {
        if (abortController.current === controller) abortController.current = null;
        inFlight.current = null;
      });

    inFlight.current = request;
    return request;
  }, [enabled, endpoint]);

  useEffect(() => {
    mounted.current = true;
    if (!enabled || !endpoint) return () => { mounted.current = false; };
    void refresh();

    const interval = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, portalConfig.pollingIntervalMs);

    const handleVisibility = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      abortController.current?.abort();
    };
  }, [enabled, endpoint, refresh]);

  return { ...status, refresh };
}
